import * as $ from "jquery";

/**
 * Press Release Autosave Functionality
 * 
 * This module handles automatic saving of press release form data
 * to prevent data loss while users are editing.
 */
export default (function () {
    // Only run on press release edit form
    const $form = $("#pressReleaseForm");
    if ($form.length === 0) return;

    // Get the press release ID from the form action URL
    const actionUrl = $form.attr("action");
    if (!actionUrl) return;

    const pressReleaseId = actionUrl.split("/").filter(Boolean).pop();
    if (!pressReleaseId) return;

    // Set up autosave endpoint URL
    const autosaveUrl = `/press-releases/${pressReleaseId}/autosave`;

    // Status element for displaying autosave status
    let $statusElement;
    
    // Create or get status element
    function getStatusElement() {
        if (!$statusElement) {
            $statusElement = $(`
                <div id="autosave-status" class="autosave-status">
                    <span class="autosave-message"></span>
                </div>
            `);
            $form.append($statusElement);
            
            // Add styles
            const style = document.createElement('style');
            style.textContent = `
                .autosave-status {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    padding: 8px 15px;
                    background-color: #fff;
                    border-radius: 4px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                    z-index: 9999;
                    transition: opacity 0.3s ease;
                    font-size: 14px;
                    opacity: 0;
                }
                .autosave-status.show {
                    opacity: 1;
                }
                .autosave-status.saving {
                    background-color: #f8f9fa;
                }
                .autosave-status.success {
                    background-color: #e8f5e9;
                }
                .autosave-status.error {
                    background-color: #ffebee;
                }
            `;
            document.head.appendChild(style);
        }
        return $statusElement;
    }

    // Show autosave status
    function showStatus(message, type = 'saving', duration = 3000) {
        const $status = getStatusElement();
        $status.removeClass('saving success error').addClass(`show ${type}`);
        $status.find('.autosave-message').html(message);
        
        if (type === 'success' || type === 'error') {
            setTimeout(() => {
                $status.removeClass('show');
            }, duration);
        }
    }

    // Get CSRF token for AJAX requests
    function getCsrfToken() {
        return $('meta[name="csrf-token"]').attr('content');
    }

    // Track if autosave is currently in progress
    let isSaving = false;

    // Track if there are pending changes to be saved
    let hasPendingChanges = false;

    // Debounce function to limit how often autosave triggers
    let debounceTimeout;
    function debounce(func, wait) {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(func, wait);
    }

    // Get form field values that need to be saved
    function getFormData() {
        const formData = new FormData($form[0]);
        
        // Very important: Remove any _method field that might be causing PATCH interpretation
        if (formData.has('_method')) {
            formData.delete('_method');
        }
        
        // Handle TinyMCE or other rich text editors if present
        if (typeof tinymce !== 'undefined' && tinymce.get('body')) {
            formData.set('body', tinymce.get('body').getContent());
        }
        
        // Handle checkboxes correctly
        formData.set('show_company_info', $('#show_company_info').is(':checked') ? 1 : 0);
        formData.set('show_contact_info', $('#show_contact_info').is(':checked') ? 1 : 0);
        
        return formData;
    }

    // Perform the actual autosave
    function performAutosave() {
        if (isSaving) {
            hasPendingChanges = true;
            return;
        }

        isSaving = true;
        showStatus('<i class="fas fa-spinner fa-spin mr-2"></i> Saving...', 'saving');

        $.ajax({
            url: autosaveUrl,
            type: 'POST', // Using type instead of method for better compatibility
            method: 'POST',
            data: getFormData(),
            processData: false,
            contentType: false,
            headers: {
                'X-CSRF-TOKEN': getCsrfToken()
            },
            success: function(response) {
                isSaving = false;
                showStatus('<i class="fas fa-check mr-2"></i> Saved', 'success');
                
                // If changes were made during saving, save again
                if (hasPendingChanges) {
                    hasPendingChanges = false;
                    setTimeout(performAutosave, 1000); // Wait a second before next save
                }
            },
            error: function(xhr) {
                isSaving = false;
                let errorMsg = 'Error saving';
                
                if (xhr.responseJSON && xhr.responseJSON.message) {
                    errorMsg = xhr.responseJSON.message;
                }
                
                showStatus('<i class="fas fa-exclamation-triangle mr-2"></i> ' + errorMsg, 'error');
                
                // If we have pending changes, try again after a delay
                if (hasPendingChanges) {
                    hasPendingChanges = false;
                    setTimeout(performAutosave, 3000); // Try again after 3 seconds
                }
            }
        });
    }

    // Trigger autosave when form fields change
    function triggerAutosave() {
        debounce(performAutosave, 2000); // Wait 2 seconds of inactivity before saving
    }

    // Initialize autosave functionality
    function init() {
        // Listen for changes on all form inputs
        $form.on('input change', 'input, select, textarea', triggerAutosave);
        
        // For TinyMCE or other rich text editors
        if (typeof tinymce !== 'undefined') {
            tinymce.on('AddEditor', function(e) {
                e.editor.on('input change', triggerAutosave);
            });
        }

        // If using Trumbowyg editor (which appears to be used in the app)
        $('.trumbowyg-editor').on('tbwchange', triggerAutosave);
        
        // Save before user navigates away
        $(window).on('beforeunload', function() {
            if (hasPendingChanges || isSaving) {
                return "You have unsaved changes. Are you sure you want to leave?";
            }
        });

        // Display initial status
        showStatus('Autosave ready', 'success', 2000);
        
        console.log('Press Release autosave initialized');
    }

    // Start the autosave system
    init();
})();
